import { Box, Chip, Typography, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useListContext, useTranslate } from 'react-admin';
import { formatNumber } from '@/helpers/format/formatNumber.js';
import { NoAnalyticsDataInfo } from '@/components/analytics/common/NoAnalyticsDataInfo.jsx';
import { NoDataPlaceholder } from '@/components/analytics/common/NoDataPlaceholder.jsx';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';
import { Palette } from '@/themes/themeOverrides/Palette';

const Percentage = ({ value }) => {
    if (!value || isNaN(value)) {
        return null;
    }

    return (
        <Chip
            label={`${value}%`}
            sx={{
                marginLeft: '12px',
                height: 20,
                borderRadius: '6px',
                '& .MuiChip-label': {
                    padding: '0 6px',
                    fontSize: '11px',
                    fontWeight: 600,
                },
            }}
        />
    );
};

const Title = () => {
    const translate = useTranslate();
    return (
        <Typography variant='h6' sx={{ marginBottom: '28px' }}>
            {translate('analytics.material.title')}
        </Typography>
    );
};

const StyledContentContainer = styled(Box)(() => ({
    display: 'flex',
    justifyContent: 'space-between',
}));

const StyledLeftBox = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    [`@media (max-width: ${breakpoints.values.md}px)`]: {
        width: '100%',
        gap: '6px',
    },
    '& .info-box--last': {
        [`@media (max-width: ${breakpoints.values.md}px)`]: {
            marginTop: '16px',
        },
    },
}));

const StyledRightBox = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    flex: 1,
    '&.chart-empty': {
        [`@media (max-width: ${breakpoints.values.sm}px)`]: {
            height: '120px !important',
        },
    },
}));

const StyledInfoBoxContainer = styled(Box)(() => ({
    minWidth: '144px',
    marginRight: '4px',
    '& .MuiTypography-caption': {
        fontSize: '14px',
        color: Palette.black,
    },
    '& .MuiTypography-root.MuiTypography-h5': {
        fontWeight: 400,
    },
}));

const StyledProgressBarContainer = styled(Box)(() => ({
    flex: '1',
    marginLeft: '16px',
    [`@media (max-width: ${breakpoints.values.md}px)`]: {
        marginLeft: '0',
        width: '100%',
    },
    '& .progress-container': {
        backgroundColor: Palette.gray.gray100,
        borderRadius: '8px',
        height: '60px',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        [`@media (max-width: ${breakpoints.values.md}px)`]: {
            height: '32px',
        },
    },
    '& .progress-bar': {
        height: '100%',
        borderRadius: '8px',
        transition: 'width 0.3s ease-in-out',
    },
}));

const StyledInfoBox = ({ label, value, total, className }) => {
    const percentage = parseFloat(((value / total) * 100).toFixed(0));

    return (
        <StyledInfoBoxContainer className={className}>
            <Typography variant='caption'>{label}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {value ? <Typography variant='h5'>{formatNumber(value)}</Typography> : <NoDataPlaceholder width='94px' height='28px' />}
                <Percentage value={percentage} />
            </Box>
        </StyledInfoBoxContainer>
    );
};

const StyledProgressBar = ({ percentage, color }) => {
    return (
        <StyledProgressBarContainer>
            <Box className='progress-container'>
                <Box
                    className='progress-bar'
                    sx={{
                        width: `${percentage}%`,
                        backgroundColor: color,
                    }}
                />
            </Box>
        </StyledProgressBarContainer>
    );
};

const StyledMaterialAnalyticsContainer = styled(Box)(() => ({
    backgroundColor: 'unset !important',
    marginTop: '32px',
    [`@media (max-width: ${breakpoints.values.md}px)`]: {
        marginTop: '20px',
    },
}));

const StyledMaterialAnalyticsBox = styled(Box)(() => ({
    padding: '40px',
    borderRadius: '16px',
    backgroundColor: Palette.white,
    width: '100%',
    [`@media (max-width: ${breakpoints.values.md}px)`]: {
        padding: '24px',
    },
    '& .progress-container + .progress-container': {
        marginTop: '24px',
    },
}));

export const TransactionMaterialAnalytics = () => {
    const { data } = useListContext();
    const isMdUp = useMediaQuery(`(min-width:${breakpoints.values.md}px)`);
    const translate = useTranslate();

    if (!data || data.length === 0) return null;

    const analytic = data[0].data;

    const isEmpty =
        !analytic.transactionsValidContainersCount || !analytic.transactionsValidContainersPetCount || !analytic.transactionsValidContainersMetalCount;
    const petPercentage = (analytic.transactionsValidContainersPetCount / analytic.transactionsValidContainersCount) * 100;
    const metalPercentage = (analytic.transactionsValidContainersMetalCount / analytic.transactionsValidContainersCount) * 100;

    const petColor = petPercentage <= metalPercentage ? Palette.blue.blue300 : Palette.blue.blue500;
    const metalColor = metalPercentage <= petPercentage ? Palette.blue.blue300 : Palette.blue.blue500;

    return (
        <StyledMaterialAnalyticsContainer>
            <StyledMaterialAnalyticsBox>
                <Title />
                <Box position='relative'>
                    <StyledContentContainer>
                        <StyledLeftBox>
                            <StyledInfoBox
                                label={translate('analytics.material.plasticBottles')}
                                value={analytic.transactionsValidContainersPetCount}
                                total={analytic.transactionsValidContainersCount}
                            />
                            {!isMdUp && <StyledProgressBar percentage={petPercentage} color={petColor} />}
                            <StyledInfoBox
                                label={translate('analytics.material.aluminumCans')}
                                value={analytic.transactionsValidContainersMetalCount}
                                total={analytic.transactionsValidContainersCount}
                                className='info-box--last'
                            />
                            {!isMdUp && <StyledProgressBar percentage={metalPercentage} color={metalColor} />}
                        </StyledLeftBox>
                        {isMdUp && (
                            <StyledRightBox className={isEmpty ? 'chart-empty' : ''}>
                                {isEmpty ? (
                                    <NoAnalyticsDataInfo />
                                ) : (
                                    <>
                                        <StyledProgressBar percentage={petPercentage} color={petColor} />
                                        <StyledProgressBar percentage={metalPercentage} color={metalColor} />
                                    </>
                                )}
                            </StyledRightBox>
                        )}
                    </StyledContentContainer>
                </Box>
            </StyledMaterialAnalyticsBox>
        </StyledMaterialAnalyticsContainer>
    );
};
